"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapQuestionStatsToRows = exports.ensureUniqueKeys = exports.getDefaultScore = exports.checkOMRData = exports.checkAttachmentData = exports.checkSurveyData = exports.getQuestionType = exports.createFreeEvalArray = exports.createEvalArray = exports.isGradeDataEntered = exports.isTestPeriodEnded = exports.calculateActualStatus = void 0;
const i18next_1 = __importDefault(require("i18next"));
const moment_1 = __importDefault(require("moment"));
const constant_1 = require("./constant");
/**
 * 서버 status가 현재 시각과 어긋날 수 있어, 프론트에서 시작일/마감일 대비
 * 현재 시각으로 진행 상태를 재계산한다.
 *
 * 안전 원칙: created / open 만 시각 기준으로 재계산하고,
 * hide / booked / closed 등 그 외 상태는 절대 변경하지 않고 그대로 반환한다.
 * (p42: 강의실 강제 종료 시 서버가 status=closed 로 확정하므로, 진행시간 내라도
 *  closed 는 종료로 유지해야 한다. enterable 은 진행시간만 계산해 별개임.)
 *
 * - status 가 created/open 이 아니면 → 그대로 반환 (closed/특수상태 보존)
 * - startline 이 무효이면 → 그대로 반환
 * - deadline 이 무효이면 → startline + duration(분) 을 마감으로 사용
 * - now < startline → created, now <= deadline → open, 그 외 → closed
 */
const calculateActualStatus = (item) => {
    const status = item?.status;
    const recalculable = [constant_1.TEST_STATUS.CREATED, constant_1.TEST_STATUS.OPEN];
    if (!recalculable.includes(status))
        return status;
    const startline = (0, moment_1.default)(item?.startline);
    if (!item?.startline || !startline.isValid())
        return status;
    let deadline = (0, moment_1.default)(item?.deadline);
    if (!item?.deadline || !deadline.isValid()) {
        deadline = startline.clone().add(item?.duration || 0, 'minutes');
    }
    const now = (0, moment_1.default)();
    if (now.isBefore(startline))
        return constant_1.TEST_STATUS.CREATED;
    if (now.isSameOrBefore(deadline))
        return constant_1.TEST_STATUS.OPEN;
    return constant_1.TEST_STATUS.CLOSED;
};
exports.calculateActualStatus = calculateActualStatus;
/**
 * 테스트 응시 기간(마감일시) 경과 여부.
 * calculateActualStatus 의 CLOSED 와 달리, 강의실 강제종료(서버 status=closed)와 무관하게
 * 실제 마감(deadline, 없으면 startline+duration)만 현재 시각과 비교한다.
 * 기타 모의고사(자동처리형) 성적표는 "응시 기간이 끝나면"에만 노출해야 하므로
 * (강제종료로 인한 응시 기간 중 조기 노출을 막기 위해) 이 기준을 사용한다.
 * - deadline / startline 모두 무효(미설정·zero-date)이면 false(보수적: 미노출)
 */
const isTestPeriodEnded = (item) => {
    let deadline = (0, moment_1.default)(item?.deadline);
    if (!item?.deadline || !deadline.isValid() || deadline.year() <= 1) {
        const startline = (0, moment_1.default)(item?.startline);
        if (!item?.startline || !startline.isValid() || startline.year() <= 1)
            return false;
        deadline = startline.clone().add(item?.duration || 0, 'minutes');
    }
    return (0, moment_1.default)().isAfter(deadline);
};
exports.isTestPeriodEnded = isTestPeriodEnded;
// 성적 데이터(컷오프 Excel) 입력 여부 판정.
// 서버 신호: has_report(=업로드되면 true) 가 정상 신호이나, 목록/아이템 응답에서 누락·미반영될 수 있어
// 마지막 저장일(report_data_saved)로 보강한다. report_data_saved 는 미등록 시 null 또는
// zero-date(0001-01-01)로 내려오므로, 유효 날짜(year>1)일 때만 입력됨으로 판정.
// (GradeDataManager 의 hasValidSavedAt / getProgressPeriodText 의 zero-date 판정과 동일 기준)
const isGradeDataEntered = (item) => {
    if (item?.has_report)
        return true;
    if (!item?.report_data_saved)
        return false;
    const saved = (0, moment_1.default)(item.report_data_saved);
    return saved.isValid() && saved.year() > 1;
};
exports.isGradeDataEntered = isGradeDataEntered;
// 해당 과목에 대한 점수와 배점 배열 생성
const createEvalArray = (scores, scoreBlank) => {
    const scoresArray = [];
    scores.forEach(score => {
        const range = score.number.split('~');
        const start = parseInt(range[0]);
        const end = parseInt(range[1]) || start;
        for (let i = start; i <= end; i++) {
            scoresArray.push({
                id: i,
                type: score.type,
                score: scoreBlank ? 0 : score.score,
                answer: '',
                examples: 5,
            });
        }
    });
    return scoresArray;
};
exports.createEvalArray = createEvalArray;
// 해당 과목에 대한 점수와 배점 배열 생성
// startNumber: 시작 문항번호(기본 1). 자유형에서 원하는 번호부터 출제 가능.
const createFreeEvalArray = (objectiveNum, subjectiveNum, startNumber = 1) => {
    const scoresArray = [];
    const total = objectiveNum + subjectiveNum;
    const start = Number.isFinite(startNumber) && startNumber > 0 ? Math.floor(startNumber) : 1;
    for (let n = 0; n < total; n++) {
        const type = n < objectiveNum ? 'objective' : 'subjective'; // objectiveNum 만큼 객관식, 나머지는 주관식
        const score = 2; // 기본값 2로 설정
        scoresArray.push({ id: start + n, type, score, answer: '', examples: 5 });
    }
    return scoresArray;
};
exports.createFreeEvalArray = createFreeEvalArray;
const getQuestionType = (text) => {
    switch (text) {
        case 'OMR(기본형)':
        case 'OMR (기본형)':
            return 'OMR';
        case 'OMR(자유형)':
        case 'OMR (자유형)':
            return 'OMR-free';
        case '파일첨부형':
            return 'attachment';
        case '설문형':
            return 'survey';
    }
};
exports.getQuestionType = getQuestionType;
const checkSurveyData = async (surveyData) => {
    if (surveyData.length === 0) {
        throw new Error('설문 항목을 추가해주세요');
    }
    let test = surveyData &&
        (await Promise.all(surveyData.map(async (item, index) => {
            if (!item)
                return;
            // 설문 제목 확인
            if (!item.title || item.title.trim() === '') {
                throw new Error('설문 제목을 입력해주세요');
            }
            // 체크박스나 선택형일 경우 설문 항목 확인
            if (item.type === 'choice' || item.type === 'checkbox') {
                if (item.data.length === 0) {
                    throw new Error('설문 항목을 추가해주세요');
                }
                // 설문 항목 내 제목 확인
                // title별 개수 집계와 빈 title 검사
                const titleCount = item.data?.reduce((acc, item) => {
                    if (!item.title || item.title.trim() === '') {
                        throw new Error('설문 항목을 입력해주세요');
                    }
                    acc[item.title] = (acc[item.title] || 0) + 1;
                    return acc;
                }, {});
                // 중복된 title이 있는지 확인
                const hasDuplicateTitle = Object.values(titleCount).some(count => count > 1);
                if (hasDuplicateTitle) {
                    throw new Error('중복된 설문 항목이 있습니다');
                }
            }
            return {
                number: index + 1,
                subject_item: undefined,
                type: item.type,
                title: item.title,
                options: item.data ? item.data.map(i => i.title) : [],
            };
        })));
    return test;
};
exports.checkSurveyData = checkSurveyData;
// 파일 첨부형 오류 확인
const checkAttachmentData = (assignmentName, attachFileList) => {
    if (attachFileList.length === 0) {
        throw new Error('첨부파일을 등록해주세요');
    }
    let test = [];
    test.push({
        title: assignmentName,
    });
    return test;
};
exports.checkAttachmentData = checkAttachmentData;
// OMR 형식 오류 확인
const checkOMRData = (omrData, subject) => {
    const test = [];
    const t = i18next_1.default.t.bind(i18next_1.default);
    // OMR 데이터 검증 함수
    const validateEvalData = i => {
        // 답변이 비어있거나 점수 또는 예시가 없는 경우 에러
        if (!i.answer || !i.examples || !i.score) {
            throw new Error(t('error.checkEvalData'));
        }
        // if (!i.score && i?.score?.toString().trim() === '') {
        // 	throw new Error(t('error.checkEvalData'));
        // }
        // 선택형일 때 답변이 범위를 벗어나는 경우 에러
        if (i.type !== 'subjective' && i.type !== 'short') {
            const answer = Number(i.answer);
            if (answer > i.examples || answer <= 0) {
                throw new Error(t('error.checkEvalData'));
            }
        }
        // 주관식일 때 점수가 음수인 경우 에러
        if (i.type === 'subjective' || i.type === 'short') {
            if (Number(i.score) < 0) {
                throw new Error(t('error.checkEvalData'));
            }
            if (i.answer.length > 300) {
                throw new Error(t('error.limit300'));
            }
        }
        // 예시가 범위를 벗어나는 경우 에러
        const examples = Number(i.examples);
        if (examples < 0 || examples > 10) {
            throw new Error(t('error.checkEvalData'));
        }
    };
    // OMR 데이터 반복 처리
    omrData.forEach(item => {
        item.data.forEach(i => {
            // 검증 로직 호출
            validateEvalData(i);
            // 데이터 저장
            test.push({
                answer: i.answer.toString(),
                number: i.id,
                point: i.score,
                subject_item: ['history', 'charater'].includes(subject.id)
                    ? t(subject.title)
                    : item.key,
                type: i.type === 'subjective' || i.type === 'short' ? 'short' : 'choice',
                examples: Number(i.examples) || 5,
            });
        });
    });
    return test;
};
exports.checkOMRData = checkOMRData;
// 기본 점수를 반환하는 함수
const getDefaultScore = (subjectId, key) => {
    let scoreTest = constant_1.koreanScores;
    if (subjectId === 'korean') {
        if (key.includes('선택'))
            return constant_1.selectKoreanScores;
    }
    else if (subjectId === 'math') {
        if (key.includes('공통'))
            return constant_1.mathScores;
        if (key.includes('선택'))
            return constant_1.selectMathScores;
    }
    else if (subjectId === 'english') {
        if (key.includes('듣기'))
            return constant_1.englishScores;
        if (key.includes('읽기'))
            return constant_1.selectEnglishScores;
    }
    else if (subjectId === 'history') {
        return constant_1.selectHistorytScore;
    }
    else if (subjectId === 'character') {
        return constant_1.selectChatacterScore;
    }
    return scoreTest;
};
exports.getDefaultScore = getDefaultScore;
function ensureUniqueKeys(data) {
    const seenKeys = new Set();
    const uniqueData = [];
    data.forEach(item => {
        if (!seenKeys.has(item.key)) {
            seenKeys.add(item.key);
            uniqueData.push(item);
        }
    });
    return uniqueData;
}
exports.ensureUniqueKeys = ensureUniqueKeys;
// question-stats API 응답 → 문항별 분석 표 rows([과목, 문항번호, 정답률, 배점, 선택지1~5]).
// 서버가 report-data 응답 형식을 재활용하므로 question_stats/items/배열 어느 래핑이든 수용하고,
// 서버가 지정한 필드(subject_item, number, rating, rank, choice1~choice5)만 사용한다.
// ★subject_item 은 실응답 기준 표시명("선택 - 언어와매체" 등, question-stats.txt 확인) —
// 숫자 과목코드가 아니므로 코드 변환 없이 그대로 쓰되, 숫자 코드로 오는 경우만 2자리 패딩
// (업로드 raws 와 동일 규칙). 헤더 행은 불필요(미리보기 필터가 데이터 행만 취함).
const mapQuestionStatsToRows = (res) => {
    const result = res?.result ?? res;
    const stats = Array.isArray(result)
        ? result
        : Array.isArray(result?.question_stats)
            ? result.question_stats
            : Array.isArray(result?.items)
                ? result.items
                : [];
    return stats
        .filter((s) => s && s.number != null)
        .map((s) => {
        const subjectRaw = String(s.subject_item ?? '').trim();
        const subject = /^\d+$/.test(subjectRaw) ? subjectRaw.padStart(2, '0') : subjectRaw;
        return [
            subject,
            s.number,
            s.rating ?? null,
            s.rank ?? null,
            s.choice1 ?? null,
            s.choice2 ?? null,
            s.choice3 ?? null,
            s.choice4 ?? null,
            s.choice5 ?? null,
        ];
    });
};
exports.mapQuestionStatsToRows = mapQuestionStatsToRows;
// (구 parseReportDataWorkbook 삭제 — download-report-data xlsx 파싱 복원 경로 폐기.
//  문항별 분석은 question-stats API, 예상등급컷은 tier-cutoff API 가 복원 소스다. 2026-07-29)
